/**
 * Coaching motif tags — classifies every move with one clear quality or error
 * verdict for the Patterns screen and move-list annotations.
 *
 * Quality (positive):  best | excellent | good
 * Error (negative):    blunder | mistake | imprecision
 * Opponent-specific:   opponent_blunder | opponent_mistake | opponent_imprecision
 *
 * Now works for both player and opponent moves since we analyze everything.
 */
import type { AnalyzedMove } from "./types";
import { primaryMotif } from "@/shared/utils/motifs";

export function deriveMotifs(
  move: Pick<
    AnalyzedMove,
    | "moveNumber"
    | "san"
    | "isPlayerMove"
    | "isBlunder"
    | "isMistake"
    | "isImprecision"
    | "centipawnLoss"
    | "evalBeforeCp"
    | "evalAfterCp"
    | "bestMoveSan"
    | "fenBefore"
    | "clockSeconds"
    | "timeSpentSeconds"
  >,
): string[] {
  const tags: string[] = [];
  if (move.san.includes("#")) return ["checkmate"];

  const cpl = move.centipawnLoss;
  const isExactBest = move.bestMoveSan != null && move.san === move.bestMoveSan;
  if (isExactBest) return ["best"];
  if (move.evalBeforeCp == null || move.evalAfterCp == null) return [];

  if (cpl < 50) {
    if (cpl <= 9) {
      tags.push("excellent");
    } else {
      tags.push("good");
    }

    return tags;
  }

  // Error severity
  // CPL is the source of truth here. The legacy boolean flags were only
  // populated for the tracked player's moves, leaving opponent plies untagged.
  if (cpl >= 200) tags.push(move.isPlayerMove ? "blunder" : "opponent_blunder");
  else if (cpl >= 100) tags.push(move.isPlayerMove ? "mistake" : "opponent_mistake");
  else tags.push(move.isPlayerMove ? "imprecision" : "opponent_imprecision");

  return [primaryMotif(tags) ?? tags[0]];
}
